import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, MapPin, User, ChevronDown, X, UserPlus, Briefcase, ArrowRight, LogOut, Edit, Building2, Users, IndianRupee, FileText } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { resolveFileUrl } from '../../utils/fileUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const MembersPage = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    name: '',
    familyName: '',
    gotra: '',
    nukh: '',
    city: ''
  });
  
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('q', filters.search);
      if (filters.name) params.append('name', filters.name);
      if (filters.familyName) params.append('familyName', filters.familyName);
      if (filters.gotra) params.append('gotra', filters.gotra);
      if (filters.nukh) params.append('nukh', filters.nukh);
      if (filters.city) params.append('city', filters.city);

      const response = await fetch(`${BACKEND_URL}/api/members/search/public?${params}`);
      if (response.ok) {
        const data = await response.json();
        setMembers(data);
      }
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchMembers();
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ search: '', name: '', familyName: '', gotra: '', nukh: '', city: '' });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchMembers();
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.gotra, filters.nukh, filters.city]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="gradient-orange py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-white/80 text-sm">
              <a href="/" className="hover:text-white">Home</a>
              <span>›</span>
              <span>Members</span>
            </div>
            
            {/* User Info & Logout for logged-in members */}
            {user && user.role !== 'admin' && (
              <div className="flex items-center gap-3">
                <span className="text-white/90 text-sm hidden sm:inline">
                  Welcome, <strong>{user.fullName}</strong>
                </span>
                <Link to="/member/profile">
                  <Button
                    variant="outline"
                    size="sm"
                    className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                    data-testid="edit-profile-link"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Profile
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                  data-testid="member-logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            )}
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            सदस्य / Members
          </h1>
          <p className="text-white/90">
            Membership categories and member directory
          </p>
        </div>
      </section>

      {/* Membership Overview Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-card border border-gray-100 overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-primary/10 to-transparent p-6 border-b border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">सदस्यता श्रेणियां</h2>
            <p className="text-gray-600">
              ब्रह्मक्षत्रिय मारवाड़ी खत्री एसोसिएशन (महासभा) के निम्नलिखित श्रेणियों के सदस्य बन सकेंगे:
            </p>
          </div>

          <div className="p-6 space-y-8">
            {/* Institutional Members */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">संस्थागत सदस्य / Institutional Members</h3>
              </div>
              <p className="text-gray-600 mb-6 pl-13">
                भारत भर की सभी ब्रह्मक्षत्रिय मारवाड़ी खत्री संस्थाएँ / समितियाँ / संगठन आदि संस्थागत सदस्य बन सकेंगे।
              </p>

              {/* Fee Structure Table */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <IndianRupee className="w-4 h-4 text-primary" />
                  संस्थागत सदस्यों (पंचायतों) के लिए पंजीकरण शुल्क संरचना
                  <span className="text-sm font-normal text-gray-500">(केवल एक बार)</span>
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-700 bg-gray-100 rounded-tl-lg">परिवार संख्या</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-700 bg-gray-100 rounded-tr-lg">पंजीकरण शुल्क</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4 text-gray-700">1 से 10 परिवार</td>
                        <td className="py-3 px-4 text-right font-medium text-primary">₹1,100</td>
                      </tr>
                      <tr className="border-b border-gray-100 bg-white">
                        <td className="py-3 px-4 text-gray-700">11 से 25 परिवार</td>
                        <td className="py-3 px-4 text-right font-medium text-primary">₹2,500</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4 text-gray-700">25 से 99 परिवार</td>
                        <td className="py-3 px-4 text-right font-medium text-primary">₹5,100</td>
                      </tr>
                      <tr className="border-b border-gray-100 bg-white">
                        <td className="py-3 px-4 text-gray-700">100 से 299 परिवार</td>
                        <td className="py-3 px-4 text-right font-medium text-primary">₹11,000</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4 text-gray-700">300 से 499 परिवार</td>
                        <td className="py-3 px-4 text-right font-medium text-primary">₹21,000</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-700">500 या अधिक परिवार</td>
                        <td className="py-3 px-4 text-right font-medium text-primary">₹25,000</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Affiliation for Other Organizations */}
            <div className="border-t border-gray-100 pt-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">समाज की अन्य संस्थाओं को सम्बद्धता</h3>
              </div>
              <p className="text-gray-600 mb-4">
                समाज की अन्य संस्थायें या ट्रस्ट जो धार्मिक, शैक्षणिक, सांस्कृतिक, खेल आदि गतिविधियों में संलग्न हैं या होंगी एसोसिएशन (महासभा) से सम्बद्धता प्राप्त कर सकेंगी।
              </p>
              <div className="bg-blue-50 rounded-lg p-4 inline-block">
                <span className="text-gray-700 font-medium">सम्बद्धता शुल्क: </span>
                <span className="text-blue-700 font-bold text-lg">₹21,000</span>
                <span className="text-gray-500 text-sm ml-1">(एक बार)</span>
              </div>
            </div>

            {/* Individual Members */}
            <div className="border-t border-gray-100 pt-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-gray-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">व्यक्तिगत सदस्य / Individual Members</h3>
              </div>
              <p className="text-gray-600 mb-4">विवरण बाद में दिया जायेगा।</p>
              <div className="flex items-center gap-3">
                <Button 
                  variant="outline" 
                  className="border-gray-300 text-gray-400 bg-gray-50 cursor-not-allowed"
                  disabled
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Become Individual Member
                </Button>
                <span className="text-sm text-gray-500 italic">(Coming Soon / जल्द आ रहा है)</span>
              </div>
            </div>

            {/* CTA */}
            <div className="border-t border-gray-100 pt-8">
              <div className="bg-gradient-to-r from-primary/5 to-transparent rounded-xl p-6 text-center">
                <p className="text-gray-700 font-medium mb-4 text-lg">
                  संस्थागत सदस्यता / सम्बद्धता के लिये पंजीकरण करने का आह्वान है।
                </p>
                <Link to="/institutional-membership" className="inline-block w-full sm:w-auto">
                  <Button className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white px-6 sm:px-8 py-3 text-base sm:text-lg whitespace-normal h-auto">
                    <Building2 className="w-5 h-5 mr-2 flex-shrink-0" />
                    Become Institutional Member
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Member Directory Section */}
      <section className="bg-gray-100 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">सदस्य निर्देशिका / Member Directory</h2>
            <p className="text-gray-600">Search and connect with registered members of our community</p>
          </div>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="flex gap-2 max-w-2xl mx-auto mb-8">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <Input
                type="text"
                placeholder="Search by name, family, gotra, nukh, or city..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-10 h-12 bg-white"
              />
            </div>
            <Button type="submit" className="h-12 px-6 bg-secondary hover:bg-secondary/90">
              Search
            </Button>
          </form>

        {/* Filter Toggle & Active Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2"
          >
            <Filter size={16} />
            Filters
            <ChevronDown size={16} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </Button>

          {(filters.gotra || filters.nukh || filters.city || filters.familyName) && (
            <>
              {filters.gotra && (
                <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  Gotra: {filters.gotra}
                  <X size={14} className="cursor-pointer" onClick={() => handleFilterChange('gotra', '')} />
                </span>
              )}
              {filters.nukh && (
                <span className="bg-green-50 text-green-600 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  Nukh: {filters.nukh}
                  <X size={14} className="cursor-pointer" onClick={() => handleFilterChange('nukh', '')} />
                </span>
              )}
              {filters.city && (
                <span className="bg-blue-50 text-blue-600 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  City: {filters.city}
                  <X size={14} className="cursor-pointer" onClick={() => handleFilterChange('city', '')} />
                </span>
              )}
              {filters.familyName && (
                <span className="bg-purple-50 text-purple-600 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  Family: {filters.familyName}
                  <X size={14} className="cursor-pointer" onClick={() => handleFilterChange('familyName', '')} />
                </span>
              )}
              <button onClick={clearFilters} className="text-sm text-gray-500 hover:text-primary">
                Clear all
              </button>
            </>
          )}

          <span className="ml-auto text-sm text-gray-500">
            {members.length} member{members.length !== 1 ? 's' : ''} found
          </span>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-white rounded-xl p-6 mb-6 shadow-sm border border-gray-100"
          >
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <Input
                  type="text"
                  placeholder="Search by name"
                  value={filters.name}
                  onChange={(e) => handleFilterChange('name', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Family Name</label>
                <Input
                  type="text"
                  placeholder="Search by family"
                  value={filters.familyName}
                  onChange={(e) => handleFilterChange('familyName', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gotra</label>
                <Input
                  type="text"
                  placeholder="Search by gotra"
                  value={filters.gotra}
                  onChange={(e) => handleFilterChange('gotra', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nukh</label>
                <Input
                  type="text"
                  placeholder="Search by nukh"
                  value={filters.nukh}
                  onChange={(e) => handleFilterChange('nukh', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <Input
                  type="text"
                  placeholder="Search by city"
                  value={filters.city}
                  onChange={(e) => handleFilterChange('city', e.target.value)}
                />
              </div>
            </div>
          </motion.div>
        )}

        {/* Results Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl">
            <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Members Found</h3>
            <p className="text-gray-500 mb-6">Try adjusting your search or filters, or become a member</p>
            <Link to="/member-registration">
              <Button className="bg-primary hover:bg-primary/90">
                <UserPlus className="w-4 h-4 mr-2" />
                Become a Member
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {members.map((member, index) => (
              <motion.div
                key={member.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                data-testid={`member-card-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden border-2 border-primary/20">
                    {member.profilePhoto ? (
                      <img
                        src={resolveFileUrl(member.profilePhoto)}
                        alt={member.fullName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-10 h-10 text-primary" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 text-lg" data-testid="member-name">{member.fullName}</h3>
                    {member.fatherOrSpouseName && (
                      <p className="text-gray-600 text-sm">
                        <span className="text-gray-400">पिता/पति:</span> {member.fatherOrSpouseName}
                      </p>
                    )}
                    <p className="text-primary font-medium text-xs mt-1 bg-primary/5 inline-block px-2 py-0.5 rounded">{member.membershipId}</p>
                  </div>
                </div>

                <div className="mt-4 space-y-2 text-sm border-t border-gray-100 pt-4">
                  {/* Nukh Badge */}
                  {member.nukh && (
                    <div className="flex items-center gap-2">
                      <span className="bg-orange-50 text-orange-700 px-3 py-1 rounded-full text-xs font-medium border border-orange-200">
                        नुख: {member.nukh}
                      </span>
                    </div>
                  )}
                  
                  {/* Location */}
                  {(member.city || member.state) && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin size={14} className="flex-shrink-0 text-primary" />
                      <span>
                        {[member.city, member.state, member.country !== 'India' ? member.country : null]
                          .filter(Boolean)
                          .join(', ')}
                      </span>
                    </div>
                  )}
                  
                  {/* Occupation */}
                  {member.occupation && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Briefcase size={14} className="flex-shrink-0 text-primary" />
                      <span>{member.occupation}</span>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-primary to-primary-600 rounded-2xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">Join Our Community</h3>
          <p className="mb-6 text-white/90">Become a member and connect with the Khatri community</p>
          <div className="flex justify-center gap-4 flex-wrap">
            <Link to="/auth">
              <Button className="bg-white text-primary hover:bg-gray-100">
                <UserPlus className="w-4 h-4 mr-2" />
                Register Now
              </Button>
            </Link>
            <Link to="/auth">
              <Button variant="outline" className="border-white text-white hover:bg-white/10">
                Already a Member? Login
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
      </section>
    </div>
  );
};

export default MembersPage;
